import { useEffect, useRef, useState } from "react";
import { Globe, Users, GraduationCap, TrendingUp, Trophy } from "lucide-react";

const defaultStats = [
  {
    icon: TrendingUp,
    number: "95%",
    label: "Client Retention",
    description: "Delivering consistent quality that builds long-term trust.",
    color: "#0058a9",
    gradient: "linear-gradient(135deg, #0058a9 0%, #1a7bc1 100%)",
    lightBg: "#aad1f2"
  },
  {
    icon: Globe,
    number: "10+",
    label: "Countries Served",
    description: "A global network providing local market expertise.",
    color: "#0d9488",
    gradient: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
    lightBg: "#b0f7e6"
  },
  {
    icon: Users,
    number: "500+",
    label: "Consultants Placed",
    description: "Connecting top-tier talent with leading organizations.",
    color: "#dc2626",
    gradient: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
    lightBg: "#f5b3b3"
  },
  {
    icon: GraduationCap,
    number: "100+",
    label: "Training Programs",
    description: "Ensuring continuous professional skill development.",
    color: "#ea580c",
    gradient: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)",
    lightBg: "#f5d3aa"
  }
];

const parseNumberValue = (numberStr) => {
  const match = numberStr.match(/\d+/);
  return match ? parseInt(match[0]) : 0;
};

const getNumberSuffix = (numberStr) => {
  if (numberStr.includes("%")) return "%";
  if (numberStr.includes("+")) return "+";
  return "";
};

function StatCard({ stat, index, shouldAnimate }) {
  const [displayNumber, setDisplayNumber] = useState(0);
  const [hasCounted, setHasCounted] = useState(false);
  const Icon = stat.icon;
  
  const targetValue = parseNumberValue(stat.number);
  const suffix = getNumberSuffix(stat.number);

  useEffect(() => {
    if (shouldAnimate && !hasCounted) {
      let startTime;
      const duration = 1500;
      
      const animateNumber = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = Math.floor(easeOutQuart * targetValue);
        
        setDisplayNumber(currentValue);
        
        if (progress < 1) {
          requestAnimationFrame(animateNumber);
        } else {
          setDisplayNumber(targetValue);
          setHasCounted(true);
        }
      };
      
      requestAnimationFrame(animateNumber);
    }
  }, [shouldAnimate, hasCounted, targetValue]);

  return (
    <div
      style={{
        animation: shouldAnimate ? `fadeInUp 0.6s ease-out ${index * 0.1}s forwards` : 'none',
        opacity: 0
      }}
    >
      <div 
        className="relative rounded-2xl p-6"
        style={{
          backgroundColor: '#ffffff',  // Each card gets its own light background color
          border: `1px solid ${stat.color}20`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        {/* Icon */}
        <div className="mb-4">
          <div 
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{
              backgroundColor:`${stat.lightBg}`,
            }}
          >
            <Icon 
              className="w-5 h-5" 
              style={{ 
                color: stat.color,
              }} 
            />
          </div>
        </div>

        {/* Number */}
        <div className="mb-1">
          <h2 
            className="text-4xl font-bold"
            style={{ 
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 700,
              color: '#081120'
            }}
          >
            {shouldAnimate ? `${displayNumber}${suffix}` : stat.number}
          </h2>
        </div>

        {/* Label */}
        <h3 
          className="text-base font-semibold mb-2"
          style={{ 
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 600,
            color: '#081120'
          }}
        >
          {stat.label}
        </h3>

        {/* Description */}
        <p 
          className="text-sm leading-relaxed"
          style={{ 
            fontFamily: '"Open Sans", sans-serif',
            color: '#6b7280'
          }}
        >
          {stat.description}
        </p>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default function OurImpact({ stats = defaultStats }) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="w-full py-20 md:py-28 lg:py-12"
      style={{ backgroundColor: "#001534" }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        
        {/* Section Header */}
        <div className="text-center mb-14">
          <h2
            className="text-4xl md:text-5xl font-semibold leading-tight mb-6"
            style={{ 
              fontFamily: 'Outfit, sans-serif',
              color: '#ffffff'
            }}
          >
            Our Impact
          </h2>

          <p
            className="text-lg md:text-xl leading-relaxed mx-auto text-center max-w-3xl"
            style={{ 
              fontFamily: '"Open Sans", sans-serif',
              color: '#ffffff',
              hyphens:"none"
            }}
          >
            Over the years, we have helped businesses grow faster, reach wider audiences, 
            and achieve strong results across the globe.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {stats.map((stat, index) => (
            <StatCard 
              key={index} 
              stat={stat} 
              index={index} 
              shouldAnimate={isVisible} 
            />
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
}